import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const ServiceabilityCheck = () => {
  const [formData, setFormData] = useState({
    sourcePincode: "",
    destinationPincode: "",
  });
  const [serviceabilityData, setServiceabilityData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [downloadProgress, setDownloadProgress] = useState(false);

  // Validate pincode format
  const validatePincode = (pincode) => {
    return /^[1-9][0-9]{5}$/.test(pincode);
  };

  const handleCheckServiceability = async () => {
    // Clear previous errors
    setFieldErrors({});
    setError("");
    setSuccessMessage("");

    // Validation
    const errors = {};
    if (!formData.sourcePincode) {
      errors.sourcePincode = "Source pincode is required";
    } else if (!validatePincode(formData.sourcePincode)) {
      errors.sourcePincode = "Invalid pincode format";
    }
    
    if (!formData.destinationPincode) {
      errors.destinationPincode = "Destination pincode is required";
    } else if (!validatePincode(formData.destinationPincode)) {
      errors.destinationPincode = "Invalid pincode format";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    setServiceabilityData(null);
    const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_CHECK_MERCHANT_SERVICEABILITY}`;
  
    try {
      const response = await axios.post(
        apiUrl,
        {
          pickup_pincode: formData.sourcePincode,
          drop_pincode: formData.destinationPincode,
        },
        {
          headers: {
            Authorization: localStorage.getItem("authToken"),
            "Content-Type": "application/json",
          },
        }
      );
  
      console.log("API Response:", response.data);
  
      if (
        !response.data.merchant_domestic_serviceabilities ||
        response.data.merchant_domestic_serviceabilities.length === 0
      ) {
        setError("No serviceability data available.");
        return;
      }
  
      const partners = response.data.merchant_domestic_serviceabilities.map((partner) => ({
        name: partner.carrier_partner_identifier_string || "N/A",
        prepaid: partner.prepaid_serviceability_details?.is_serviceable ?? false,
        cod: partner.cod_serviceability_details?.is_serviceable ?? false,
      }));
  
      setServiceabilityData({
        isServiceable: partners.length > 0,
        partners,
      });
      setSuccessMessage("Serviceability check completed successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error fetching serviceability:", err);
      setError("Failed to fetch serviceability data. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDownloadList = () => {
    if (!serviceabilityData || !serviceabilityData.partners) {
      setError("No data available to download.");
      return;
    }

    setDownloadProgress(true);

    // Convert data to CSV format
    const csvRows = [];
    const headers = ["Courier Partner", "Prepaid", "COD"];
    csvRows.push(headers.join(","));

    serviceabilityData.partners.forEach((partner) => {
      const row = [
        partner.name,
        partner.prepaid ? "Yes" : "No",
        partner.cod ? "Yes" : "No",
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    // Trigger download
    const link = document.createElement("a");
    link.href = url;
    const timestamp = new Date().toISOString().split('T')[0];
    link.download = `serviceability_${formData.sourcePincode}_to_${formData.destinationPincode}_${timestamp}.csv`;
    link.click();

    // Cleanup
    URL.revokeObjectURL(url);
    
    setTimeout(() => {
      setDownloadProgress(false);
      setSuccessMessage("File downloaded successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    }, 500);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white shadow-xl sm:shadow-2xl rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 lg:p-8 w-full mx-auto border border-gray-100"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Serviceability Check</h3>
          <p className="text-xs sm:text-sm text-gray-600">Verify courier partner availability for your shipping route</p>
        </div>
        <div className="hidden sm:block">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white p-2 sm:p-3 lg:p-4 rounded-lg sm:rounded-xl shadow-lg">
            <span className="text-2xl">🚚</span>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="relative">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Source Pincode <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-base sm:text-lg">📍</span>
            <input
              type="text"
              name="sourcePincode"
              placeholder="e.g., 110001"
              value={formData.sourcePincode}
              onChange={handleInputChange}
              maxLength={6}
              className={`w-full border ${
                fieldErrors.sourcePincode ? "border-red-500" : "border-gray-300"
              } rounded-lg sm:rounded-xl pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base`}
            />
          </div>
          {fieldErrors.sourcePincode && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-500 text-xs sm:text-sm mt-1"
            >
              {fieldErrors.sourcePincode}
            </motion.p>
          )}
        </div>

        <div className="relative">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Destination Pincode <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-base sm:text-lg">🎯</span>
            <input
              type="text"
              name="destinationPincode"
              placeholder="e.g., 400001"
              value={formData.destinationPincode}
              onChange={handleInputChange}
              maxLength={6}
              className={`w-full border ${
                fieldErrors.destinationPincode ? "border-red-500" : "border-gray-300"
              } rounded-lg sm:rounded-xl pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base`}
            />
          </div>
          {fieldErrors.destinationPincode && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-500 text-xs sm:text-sm mt-1"
            >
              {fieldErrors.destinationPincode}
            </motion.p>
          )}
        </div>

        <div className="flex items-end sm:col-span-2 lg:col-span-1">
          <motion.button
            onClick={handleCheckServiceability}
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: loading ? 1 : 0.98 }}
            className={`w-full ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            } text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold shadow-lg transition-all duration-200 flex items-center justify-center gap-2 min-h-[52px] text-sm sm:text-base`}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm sm:text-base">Checking...</span>
              </>
            ) : (
              <>
                <span className="text-sm sm:text-base">Check Serviceability</span>
                <span>🔍</span>
              </>
            )}
          </motion.button>
        </div>
      </div>

      {/* Success Message */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-3 sm:mb-4 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg sm:rounded-xl flex items-center gap-2 sm:gap-3"
          >
            <span className="text-green-600 text-lg sm:text-xl">✓</span>
            <span className="text-green-800 font-medium text-xs sm:text-sm">{successMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-3 sm:mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg sm:rounded-xl flex items-center gap-2 sm:gap-3"
          >
            <span className="text-red-600 text-lg sm:text-xl">⚠</span>
            <span className="text-red-800 font-medium text-xs sm:text-sm">{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {serviceabilityData && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Summary Card */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="bg-white p-2 sm:p-3 rounded-lg sm:rounded-xl shadow-sm">
                  <span className="text-xl">
                    {serviceabilityData.isServiceable ? "✅" : "❌"}
                  </span>
                </div>
                <div>
                  <p className="text-gray-800 text-sm sm:text-base lg:text-lg mb-0.5 sm:mb-1">
                    Route Status:{" "}
                    <span className={`font-bold ${serviceabilityData.isServiceable ? "text-green-600" : "text-red-600"}`}>
                      {serviceabilityData.isServiceable ? "Serviceable!" : "Not Serviceable"}
                    </span>
                  </p>
                  <p className="text-gray-600 text-xs sm:text-sm">
                    <span className="font-semibold text-blue-600">{serviceabilityData.partners.length}</span>{" "}
                    courier partner{serviceabilityData.partners.length !== 1 ? "s are" : " is"} serving this route
                  </p>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-1.5 sm:mt-2">
                    <div className="bg-white px-2 sm:px-3 py-1 rounded-md sm:rounded-lg text-xs sm:text-sm">
                      <span className="text-gray-500">From:</span>{" "}
                      <span className="font-semibold text-gray-800">{formData.sourcePincode}</span>
                    </div>
                    <div className="bg-white px-2 sm:px-3 py-1 rounded-md sm:rounded-lg text-xs sm:text-sm">
                      <span className="text-gray-500">To:</span>{" "}
                      <span className="font-semibold text-gray-800">{formData.destinationPincode}</span>
                    </div>
                  </div>
                </div>
              </div>
              <motion.button
                onClick={handleDownloadList}
                disabled={downloadProgress}
                whileHover={{ scale: downloadProgress ? 1 : 1.05 }}
                whileTap={{ scale: downloadProgress ? 1 : 0.95 }}
                className={`${
                  downloadProgress
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                } text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold shadow-lg transition-all duration-200 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm min-h-[44px] sm:min-h-[48px] w-full md:w-auto justify-center`}
              >
                {downloadProgress ? (
                  <>
                    <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Downloading...</span>
                  </>
                ) : (
                  <>
                    <span>📥</span>
                    <span>Download Report</span>
                  </>
                )}
              </motion.button>
            </div>
          </div>

          {/* Partners Table */}
          <div className="overflow-hidden rounded-xl sm:rounded-2xl border border-gray-200 shadow-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wider">
                      Courier Partner
                    </th>
                    <th className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wider">
                      Prepaid
                    </th>
                    <th className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wider">
                      COD
                    </th>
                    <th className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {serviceabilityData.partners.map((partner, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-blue-50 transition-colors duration-150"
                    >
                      <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center font-bold shadow-sm flex-shrink-0">
                            {partner.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-2 sm:ml-4">
                            <div className="text-xs sm:text-sm font-bold text-gray-900">{partner.name}</div>
                            <div className="text-xs text-gray-500">Courier Partner</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-center">
                        {partner.prepaid ? (
                          <div className="inline-flex items-center gap-1 sm:gap-2 bg-green-100 text-green-800 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm">
                            <span className="text-green-600 text-sm sm:text-lg">✓</span>
                            <span className="hidden sm:inline">Available</span>
                            <span className="sm:hidden">Yes</span>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1 sm:gap-2 bg-red-100 text-red-800 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm">
                            <span className="text-red-600 text-sm sm:text-lg">✗</span>
                            <span className="hidden sm:inline">Unavailable</span>
                            <span className="sm:hidden">No</span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-center">
                        {partner.cod ? (
                          <div className="inline-flex items-center gap-1 sm:gap-2 bg-green-100 text-green-800 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm">
                            <span className="text-green-600 text-sm sm:text-lg">✓</span>
                            <span className="hidden sm:inline">Available</span>
                            <span className="sm:hidden">Yes</span>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1 sm:gap-2 bg-red-100 text-red-800 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm">
                            <span className="text-red-600 text-sm sm:text-lg">✗</span>
                            <span className="hidden sm:inline">Unavailable</span>
                            <span className="sm:hidden">No</span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-center">
                        {partner.prepaid || partner.cod ? (
                          <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 sm:px-3 py-1 rounded-full text-xs font-bold">
                            <span>🚀</span>
                            <span>Active</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 px-2 sm:px-3 py-1 rounded-full text-xs font-bold">
                            <span>⏸</span>
                            <span>Inactive</span>
                          </span>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4 mt-4 sm:mt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border border-blue-200"
            >
              <div className="text-xl mb-2">📦</div>
              <div className="text-lg font-bold text-blue-900">
                {serviceabilityData.partners.filter(p => p.prepaid).length}
              </div>
              <div className="text-xs sm:text-sm text-blue-700 font-medium">Prepaid Available</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-green-50 to-green-100 p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border border-green-200"
            >
              <div className="text-xl mb-2">💵</div>
              <div className="text-lg font-bold text-green-900">
                {serviceabilityData.partners.filter(p => p.cod).length}
              </div>
              <div className="text-xs sm:text-sm text-green-700 font-medium">COD Available</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-purple-50 to-purple-100 p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border border-purple-200"
            >
              <div className="text-xl mb-2">🎯</div>
              <div className="text-lg font-bold text-purple-900">
                {serviceabilityData.partners.length}
              </div>
              <div className="text-xs sm:text-sm text-purple-700 font-medium">Total Partners</div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ServiceabilityCheck;
